import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Wine, ChefHat, PartyPopper, Clock, Users, Check, Send, ChevronRight, Loader2 } from 'lucide-react';
import { teamBuildingOptions, images } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  'wine-tasting': Wine,
  'cooking-class': ChefHat,
  'private-event': PartyPopper,
};

const EventsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    participants: '',
    date: '',
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const { t, lang } = useLanguage();
  const L = (obj, f) => (lang === 'en' && obj[`${f}_en`]) || obj[f];

  React.useEffect(() => {
    document.title = 'Événements & Team Building - Ya Habibi! Restaurant Libanais Genève';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/events/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          event_type: formData.eventType,
          participants: formData.participants,
          date: formData.date,
          message: formData.message,
        }),
      });
      if (!res.ok) throw new Error('Erreur serveur');
      toast.success(t('events', 'successMsg'));
      setFormData({ name: '', email: '', phone: '', eventType: '', participants: '', date: '', message: '' });
    } catch {
      toast.error(t('events', 'errorMsg'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen pt-24" data-testid="events-page">
      <section className="bg-[#F5E6D3] py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#0f172a] mb-4">{t('events', 'title')}</h1>
          <p className="text-lg text-[#0f172a]/80 max-w-2xl mx-auto">{t('events', 'subtitle')}</p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {teamBuildingOptions.map((option) => {
              const Icon = iconMap[option.id];
              return (
                <div key={option.id} className="bg-[#f8fafc] rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 group" data-testid={`event-card-${option.id}`}>
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={images[option.image]} alt={L(option, 'name')} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-[#F5E6D3] rounded-full flex items-center justify-center">
                        {Icon && <Icon size={24} className="text-[#8B4513]" />}
                      </div>
                      <h3 className="font-serif text-xl font-bold text-[#0f172a]">{L(option, 'name')}</h3>
                    </div>
                    <p className="text-[#64748b] mb-6 leading-relaxed">{L(option, 'description')}</p>
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center gap-2 text-[#0f172a]">
                        <Clock size={18} className="text-[#D4A853]" />
                        <span>{L(option, 'duration')}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#0f172a]">
                        <Users size={18} className="text-[#D4A853]" />
                        <span>{option.minParticipants} - {option.maxParticipants} {t('events', 'persons')}</span>
                      </div>
                    </div>
                    <div className="mb-6">
                      <p className="font-semibold text-[#0f172a] mb-2">{t('events', 'included')}</p>
                      <ul className="space-y-2">
                        {L(option, 'includes').map((item, index) => (
                          <li key={index} className="flex items-center gap-2 text-[#64748b]">
                            <Check size={16} className="text-[#D4A853]" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-xl font-bold text-[#D4A853]">{L(option, 'pricePerPerson')}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#f8fafc]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#0f172a] mb-4">{t('events', 'requestQuote')}</h2>
            <p className="text-lg text-[#64748b]">{t('events', 'quoteSubtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-lg" data-testid="quote-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[#0f172a] font-medium mb-2">{t('events', 'fullName')}</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5E6D3] focus:border-transparent outline-none transition-all" data-testid="quote-name-input" />
              </div>
              <div>
                <label className="block text-[#0f172a] font-medium mb-2">{t('events', 'email')}</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5E6D3] focus:border-transparent outline-none transition-all" data-testid="quote-email-input" />
              </div>
              <div>
                <label className="block text-[#0f172a] font-medium mb-2">{t('events', 'phone')}</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5E6D3] focus:border-transparent outline-none transition-all" placeholder="+41 XX XXX XX XX" data-testid="quote-phone-input" />
              </div>
              <div>
                <label className="block text-[#0f172a] font-medium mb-2">{t('events', 'eventType')}</label>
                <select name="eventType" value={formData.eventType} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5E6D3] focus:border-transparent outline-none transition-all bg-white" data-testid="quote-eventtype-select">
                  <option value="">{t('events', 'select')}</option>
                  {teamBuildingOptions.map((option) => (
                    <option key={option.id} value={option.id}>{L(option, 'name')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[#0f172a] font-medium mb-2">{t('events', 'participants')}</label>
                <input type="number" name="participants" value={formData.participants} onChange={handleChange} required min="1" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5E6D3] focus:border-transparent outline-none transition-all" placeholder="Ex: 10" data-testid="quote-participants-input" />
              </div>
              <div>
                <label className="block text-[#0f172a] font-medium mb-2">{t('events', 'desiredDate')}</label>
                <input type="date" name="date" value={formData.date} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5E6D3] focus:border-transparent outline-none transition-all" data-testid="quote-date-input" />
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-[#0f172a] font-medium mb-2">{t('events', 'messageLabel')}</label>
              <textarea name="message" value={formData.message} onChange={handleChange} rows="4" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5E6D3] focus:border-transparent outline-none transition-all resize-none" placeholder={t('events', 'messagePlaceholder')} data-testid="quote-message-input" />
            </div>

            <button type="submit" disabled={isLoading} className="w-full mt-8 bg-[#0f172a] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#1e293b] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="quote-submit-button">
              {isLoading ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
              {isLoading ? t('events', 'sending') : t('events', 'submit')}
            </button>
          </form>
        </div>
      </section>

      <section className="py-16 bg-[#F5E6D3]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl font-bold text-[#0f172a] mb-4">{t('events', 'question')}</h2>
          <p className="text-lg text-[#0f172a]/80 mb-8">{t('events', 'questionSubtitle')}</p>
          <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-[#0f172a] text-white px-8 py-4 rounded-md font-semibold hover:bg-[#1e293b] transition-all duration-200 hover:-translate-y-1 shadow-lg">
            {t('events', 'contactUs')}
            <ChevronRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
